// external
import { writable } from 'svelte/store';
// lib
import { ajaxRequestor, getApiHost } from '../../../utils/index.js';
import { RetailerPartnerType } from '../enum/index.js';
// data
import { locationEndpoints } from '../data/index.js';
// utils
import { retailerResponseParser } from '../utils/index.js';
const DEFAULT_MAX_LOCATIONS = 10;
let state = {
    endpoint: '',
    retailerIntegration: RetailerPartnerType.NONE,
    currentLocation: undefined,
    locations: [],
    maxStoreLocations: DEFAULT_MAX_LOCATIONS,
    requestedLocationObject: {
        city: '',
        state: '',
        stateShort: '',
        zip: ''
    },
    productIntegrationsRetailerLocationsApiResponse: {},
};
const store = writable(state);
let currentLocationIndex = 0;
/**
 * `init` initializes the store with additional
 * ILocationsState properties.
 * @param {ILocationState} config: Partial<{@link ILocationState}>
 * @returns Promise<void>
 */
const init = async (config, fetchImmediatly = true) => {
    try {
        const endpoint = config.endpoint
            ? config.endpoint
            : `${getApiHost()}${locationEndpoints[config.retailerIntegration || RetailerPartnerType.NONE]}`;
        state = {
            ...state,
            ...config,
            endpoint,
            currentLocation: state.locations.length > 0 ? state.locations[0] : undefined
        };
        store.set(state);
        if (fetchImmediatly)
            await fetchLocations();
    }
    catch (err) {
        throw err;
    }
};
/**
 * `fetchLocations` calls the locations api to retrieve a list
 * of retailer store locations
 * @param {number} max - limit of locations to return (default = 5)
 * @param {string} zip - optional zip code to use to search for stores
 * @param {ICoordinates} coordinates - optional coordinates to use to search for stores
 */
const fetchLocations = async (max, zip, coordinates) => {
    if (state.endpoint === '') {
        throw new Error(`ERROR: no locations endpoint available for ${state.retailerIntegration}`);
    }
    // If there are coordinates, we will use the `lat/lon` parameters, otherwise use `zip`, or we default to no parameters in the URL
    const url = coordinates
        ? `${state.endpoint}?lat=${coordinates.latitude}&lon=${coordinates.longitude}`
        : zip
            ? `${state.endpoint}?zip=${zip}`
            : state.endpoint;
    const limit = max ? max : state.maxStoreLocations;
    const options = {
        body: JSON.stringify({ limit })
    };
    const locationsResponse = await ajaxRequestor.post(url, options, true);
    state.productIntegrationsRetailerLocationsApiResponse = locationsResponse;
    const requestedLocationObject = locationsResponse.requestedLocation;
    const locations = retailerResponseParser.normalizeLocations(locationsResponse.locations, state.retailerIntegration);
    const currentLocation = locations.length > 0 ? locations[0] : undefined;
    state = {
        ...state,
        currentLocation,
        locations,
        requestedLocationObject
    };
    store.set(state);
};
/**
 * `updateCurrentLocation` sets the current location to
 * a new item from the state's locations array
 * @param {number} index - the new location's index
 * @returns
 */
const updateCurrentLocation = (index) => {
    if (index === currentLocationIndex) {
        return;
    }
    if (index > state.locations.length - 1) {
        return;
    }
    state.currentLocation = state.locations[index];
    currentLocationIndex = index;
    store.set(state);
};
const getLocationsApiResponse = () => {
    return state.productIntegrationsRetailerLocationsApiResponse;
};
const { set, subscribe, update } = store;
export default {
    set,
    subscribe,
    update,
    init,
    fetchLocations,
    getLocationsApiResponse,
    updateCurrentLocation
};
